<template>
  <div class="purchase-order-list" :class="{ 'purchase-order-list--embedded': embedded }">
    <breadcrumb v-if="!embedded" :items="breadcrumbs"></breadcrumb>

    <component :is="embedded ? 'div' : 'v-container'" :fluid="!embedded">
      <div v-if="!embedded" class="d-flex mb-4 flex-wrap">
        <h2 class="font-weight-bolder">{{ purchaseOrderLabel }}</h2>
        <v-spacer></v-spacer>
        <v-btn
          v-if="getModulePermittedActions.post"
          color="primary"
          dark
          class="mr-2 mb-2 text-uppercase"
          @click="openAdd"
        >
          {{ addPurchaseOrderLabel }}
        </v-btn>
      </div>

      <component :is="embedded ? 'div' : 'v-card'" elevation="0" :class="embedded ? '' : 'px-5'">
        <component :is="embedded ? 'div' : 'v-card-text'">
          <v-data-table
            ref="listTable"
            :headers="tableHeaders"
            :items="records"
            :items-per-page="options.itemsPerPage"
            :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
            item-key="id"
            hide-default-footer
            :loading="loading"
            :options.sync="options"
            :server-items-length="totalRecords"
            :loading-text="$t('loadingData')"
            class="purchase-order-list-table"
          >
            <template v-slot:item.orderId="{ item }">
              {{ item.orderCode || "—" }}
            </template>

            <template v-slot:item.farmFarmer="{ item }">
              <div>{{ item.userFarms?.farmName || "—" }}</div>
              <div>{{ formatFarmerName(item.farmer) }}</div>
            </template>

            <template v-slot:item.coffeeSpecies="{ item }">
              <div>{{ item.coffeeSpecies?.name || "—" }}</div>
              <div>{{ formatVarietyNames(item) }}</div>
            </template>

            <template v-slot:item.coffeeQtyQuality="{ item }">
              <div>{{ item.coffeeCherryQty != null ? `${item.coffeeCherryQty} kg` : "—" }}</div>
              <div>{{ item.coffeeCherryQlty || "—" }}</div>
            </template>

            <template v-slot:item.purchaseDate="{ item }">
              {{ formatPurchaseDate(item.purchasedAt) }}
            </template>

            <template v-slot:item.action="{ item }">
              <v-menu offset-y left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="viewRecord(item)">
                    <v-list-item-title>{{ $t("memberDataSection.views") }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    :disabled="!getModulePermittedActions.put"
                    @click="editRecord(item)"
                  >
                    <v-list-item-title>{{ $t("memberDataSection.edit") }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    :disabled="!getModulePermittedActions.delete"
                    @click="deleteRecord(item)"
                  >
                    <v-list-item-title class="red--text">{{ $t("memberDataSection.delete") }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>

            <template v-slot:top>
              <div class="d-flex mb-4 flex-wrap align-center" :class="{ 'mt-2': embedded }">
                <div class="ml-3" style="width: 280px;">
                  <v-text-field
                    v-model="search"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    :placeholder="$t('memberDataSection.search')"
                    hide-details
                    @input="onSearch"
                  ></v-text-field>
                </div>
                <v-spacer></v-spacer>
                <div class="d-flex align-center mb-3">
                  <div class="py-1 title">
                    {{ fromRecord }} - {{ toRecord }} {{ $t("of") }} {{ totalRecords }}
                  </div>
                  <v-btn
                    class="mx-2"
                    fab
                    small
                    outlined
                    color="primary"
                    :disabled="options.page <= 1"
                    @click="pageChange(false)"
                  >
                    <v-icon dark>mdi-chevron-left</v-icon>
                  </v-btn>
                  <v-btn
                    class="mx-2"
                    fab
                    small
                    outlined
                    color="primary"
                    :disabled="options.page >= totalPages"
                    @click="pageChange(true)"
                  >
                    <v-icon dark>mdi-chevron-right</v-icon>
                  </v-btn>
                  <v-menu
                    v-model="customizeMenu"
                    :close-on-content-click="false"
                    :nudge-width="200"
                    left
                    :min-width="417"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn class="mx-2" fab small outlined color="primary" v-bind="attrs" v-on="on">
                        <v-icon dark>mdi-cog-outline</v-icon>
                      </v-btn>
                    </template>
                    <v-card width="417">
                      <v-list>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>{{ $t("deforestation.customization") }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-divider></v-divider>
                        <div class="pl-3 pt-2 font-weight-normal">
                          {{ $t("deforestation.recordsPerpage") }}
                        </div>
                        <v-list-item>
                          <v-list-item-content>
                            <v-slider
                              v-model="itemsPerPageSlider"
                              :tick-labels="itemsPerPageOptions"
                              :max="3"
                              step="1"
                              ticks="always"
                              tick-size="4"
                            ></v-slider>
                          </v-list-item-content>
                        </v-list-item>
                        <v-divider class="mt-n3"></v-divider>
                      </v-list>
                      <v-card-text>
                        <v-container fluid>
                          <v-row>
                            <v-col
                              v-for="header in defaultHeaders"
                              :key="header.value"
                              class="mt-n9"
                              cols="6"
                            >
                              <v-checkbox
                                v-if="header.text"
                                v-model="selectedHeaderLabels"
                                :label="header.text"
                                color="primary"
                                :value="header.text"
                                hide-details
                                :disabled="header.value === 'orderId'"
                              ></v-checkbox>
                            </v-col>
                          </v-row>
                        </v-container>
                      </v-card-text>
                      <v-layout justify-center>
                        <v-card-actions class="mb-5">
                          <v-spacer></v-spacer>
                          <v-btn outlined color="primary" width="190" @click="resetDefault">
                            <span class="text-truncate" style="max-width: 150px">
                              {{ $t("deforestation.restoreDefault") }}
                            </span>
                          </v-btn>
                          <v-btn color="primary" width="190" @click="applyTableCustomization">
                            {{ $t("deforestation.apply") }}
                          </v-btn>
                        </v-card-actions>
                      </v-layout>
                    </v-card>
                  </v-menu>
                </div>
              </div>
            </template>

            <template v-slot:footer></template>
          </v-data-table>
        </component>
      </component>
    </component>

    <PurchaseOrderAddDialog
      v-model="addDialogVisible"
      :buying-station-id="resolvedBuyingStationId"
      @save="onPurchaseOrderSaved"
      @close="onAddClose"
    />

    <PurchaseOrderDetailDialog
      v-model="detailDialogVisible"
      :record="viewingRecord"
      @edit="onDetailEdit"
      @close="onDetailClose"
    />

    <PurchaseOrderEditDialog
      v-model="editDialogVisible"
      mode="edit"
      :record="editingRecord"
      :buying-station-id="resolvedBuyingStationId"
      @save="onPurchaseOrderSaved"
      @close="onEditClose"
    />
  </div>
</template>

<script>
import { isKenyaClient } from "@/utils";
import moment from "moment";
import BuyingStationService from "@/_services/BuyingStationService";
import getPermittedActions from "@/mixins/GetPermittedActions";
import PurchaseOrderAddDialog from "./components/AddDialog.vue";
import PurchaseOrderEditDialog from "./components/EditDialog.vue";
import PurchaseOrderDetailDialog from "./components/DetailDialog.vue";

const ITEMS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

export default {
  name: "PurchaseOrderList",
  components: {
    PurchaseOrderAddDialog,
    PurchaseOrderEditDialog,
    PurchaseOrderDetailDialog,
  },
  mixins: [getPermittedActions],
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
    buyingStationId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      addDialogVisible: false,
      detailDialogVisible: false,
      viewingRecord: null,
      editDialogVisible: false,
      editingRecord: null,
      search: "",
      debounceTimer: null,
      records: [],
      loading: false,
      options: { page: 1, itemsPerPage: 10, sortBy: [], sortDesc: [] },
      fromRecord: 0,
      toRecord: 0,
      totalRecords: 0,
      totalPages: 1,
      customizeMenu: false,
      itemsPerPageOptions: ITEMS_PER_PAGE_OPTIONS,
      itemsPerPageSlider: 1,
      tableHeaders: [],
      selectedHeaderLabels: [],
      ignoreOptionsWatcherOnce: false,
    };
  },
  computed: {
    resolvedBuyingStationId() {
      return this.buyingStationId || this.$route.params.id;
    },
    purchaseOrderLabel() {
      return isKenyaClient() ? this.$t("memberDataSection.cherryCollection") : this.$t("memberDataSection.purchaseOrder");
    },
    addPurchaseOrderLabel() {
      return isKenyaClient() ? this.$t("memberDataSection.addCherryCollection") : this.$t("memberDataSection.addPurchaseOrder");
    },
    breadcrumbs() {
      return [
        { text: this.$t("dashboard"), disabled: false, to: { name: "AdminDashboard" }, exact: true },
        { text: this.$t("dashboardSection.cropOverview"), disabled: true, href: "#" },
        { text: this.$t("memberDataSection.buyingStation"), disabled: false, to: { name: "CoffeeBuyingStation" }, exact: true },
        { text: this.purchaseOrderLabel, disabled: true, href: "#" },
      ];
    },
    defaultHeaders() {
      return [
        {
          text: this.$t("memberDataSection.orderId"),
          align: "start",
          value: "orderId",
          class: "black--text",
          sortable: true,
        },
        {
          text: this.$t("memberDataSection.farmFarmer"),
          align: "start",
          value: "farmFarmer",
          class: "black--text",
          sortable: false,
        },
        {
          text: this.$t("memberDataSection.coffeeSpeciesVariety"),
          align: "start",
          value: "coffeeSpecies",
          class: "black--text",
          sortable: false,
        },
        {
          text: this.$t("memberDataSection.coffeeQtyQuality"),
          align: "start",
          value: "coffeeQtyQuality",
          class: "black--text",
          sortable: false,
        },
        {
          text: this.$t("memberDataSection.purchaseDate"),
          align: "start",
          value: "purchaseDate",
          class: "black--text",
          sortable: true,
        },
        { align: "center", value: "action", sortable: false, width: "72px" },
      ];
    },
    allHeaderLabels() {
      return this.defaultHeaders.filter((h) => h.text).map((h) => h.text);
    },
  },
  watch: {
    resolvedBuyingStationId() {
      this.options = { ...this.options, page: 1 };
      this.fetchList();
    },
    options: {
      handler(opt) {
        if (this.ignoreOptionsWatcherOnce) {
          this.ignoreOptionsWatcherOnce = false;
          return;
        }
        this.fetchList(opt);
      },
      deep: true,
    },
  },
  mounted() {
    this.tableHeaders = [...this.defaultHeaders];
    this.selectedHeaderLabels = [...this.allHeaderLabels];
    this.ignoreOptionsWatcherOnce = true;
    this.fetchList();
  },
  methods: {
    openAdd() {
      this.addDialogVisible = true;
    },
    viewRecord(item) {
      if (!item) return;
      this.viewingRecord = item;
      this.detailDialogVisible = true;
    },
    onDetailClose() {
      this.viewingRecord = null;
    },
    onDetailEdit(record) {
      this.detailDialogVisible = false;
      this.viewingRecord = null;
      this.editRecord(record);
    },
    editRecord(item) {
      if (!item) return;
      this.editingRecord = {
        ...item,
        id: item.orderCode || item.id,
        orderCode: item.orderCode || item.id,
      };
      this.editDialogVisible = true;
    },
    onAddClose() {
      this.addDialogVisible = false;
    },
    onEditClose() {
      this.editingRecord = null;
      this.editDialogVisible = false;
    },
    onPurchaseOrderSaved() {
      this.addDialogVisible = false;
      this.editDialogVisible = false;
      this.editingRecord = null;
      this.refresh();
    },
    async deleteRecord(item) {
      const id = item?.id;
      if (id == null) return;
      try {
        await BuyingStationService.deletePurchaseOrder(id);
        this.$notify({
          type: "success",
          title: this.$t("success"),
          text: this.$t("memberDataSection.purchaseOrderDetail.deleteSuccess"),
        });
        this.refresh();
      } catch (err) {
        this.$notify({
          type: "error",
          text: err?.data?.message || err?.message || this.$t("error"),
        });
      }
    },
    formatFarmerName(farmer) {
      if (!farmer) return "—";
      return [farmer.firstName, farmer.middleName, farmer.lastName].filter(Boolean).join(" ") || "—";
    },
    formatVarietyNames(item) {
      return (
        item.coffeeVarietyDirect?.name ||
        (Array.isArray(item.coffeeVariety) ? item.coffeeVariety.map((v) => v.name).join(", ") : "") ||
        "—"
      );
    },
    formatPurchaseDate(date) {
      if (!date) return "—";
      if (moment(date, "MM/DD/YYYY", true).isValid()) {
        return moment(date, "MM/DD/YYYY").format("DD/MM/YYYY");
      }
      if (moment(date).isValid()) {
        return moment(date).format("DD/MM/YYYY");
      }
      return date;
    },
    onSearch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.options = { ...this.options, page: 1 };
        this.fetchList();
      }, 500);
    },
    pageChange(forward) {
      const page = forward ? this.options.page + 1 : this.options.page - 1;
      this.options = { ...this.options, page };
    },
    resetDefault() {
      this.tableHeaders = [...this.defaultHeaders];
      this.selectedHeaderLabels = [...this.allHeaderLabels];
      this.itemsPerPageSlider = 1;
      this.options = { ...this.options, itemsPerPage: 10 };
      this.customizeMenu = false;
      this.fetchList();
    },
    applyTableCustomization() {
      this.tableHeaders = this.defaultHeaders.filter((header) =>
        this.selectedHeaderLabels.includes(header.text)
      );
      if (!this.tableHeaders.some((h) => h.value === "action")) {
        this.tableHeaders = [...this.tableHeaders, this.defaultHeaders[this.defaultHeaders.length - 1]];
      }
      this.options = {
        ...this.options,
        itemsPerPage: this.itemsPerPageOptions[this.itemsPerPageSlider],
        page: 1,
      };
      this.customizeMenu = false;
      this.fetchList();
    },
    refresh() {
      this.fetchList();
    },
    async fetchList(opt = null) {
      const stationId = this.resolvedBuyingStationId;
      if (!stationId) return;

      this.loading = true;
      const requestOptions = {
        page: this.options.page || 1,
        itemsPerPage: this.options.itemsPerPage || 10,
        limit: this.options.itemsPerPage || 10,
        sortBy: this.options.sortBy,
        sortDesc: this.options.sortDesc,
      };

      if (opt?.sortBy?.length) {
        requestOptions.sortBy = opt.sortBy;
        requestOptions.sortDesc = opt.sortDesc;
      }

      try {
        const res = await BuyingStationService.getSingleBuyingStationPurchaseOrder(
          stationId,
          requestOptions,
          this.search
        );

        if (res?.code === 200 && res?.data) {
          this.records = res.data.rows || [];
          this.totalRecords = res.data.totalRows || 0;
          this.totalPages = Math.ceil(this.totalRecords / this.options.itemsPerPage) || 1;
          this.fromRecord = this.totalRecords
            ? (this.options.page - 1) * this.options.itemsPerPage + 1
            : 0;
          this.toRecord = this.totalRecords
            ? Math.min(this.options.page * this.options.itemsPerPage, this.totalRecords)
            : 0;
        } else {
          this.records = [];
          this.totalRecords = 0;
          this.fromRecord = 0;
          this.toRecord = 0;
          this.totalPages = 1;
        }
      } catch (err) {
        this.records = [];
        this.totalRecords = 0;
        this.fromRecord = 0;
        this.toRecord = 0;
        this.totalPages = 1;
        this.$notify({
          type: "error",
          text: err?.data?.message || err?.message || this.$t("memberDataSection.purchaseOrderDetail.fetchFailed"),
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.purchase-order-list-table ::v-deep thead th {
  background-color: #1b5e20 !important;
}

.purchase-order-list-table ::v-deep thead th span {
  color: #fff !important;
  text-transform: uppercase;
  font-weight: 600;
}

.purchase-order-list-table ::v-deep tbody td {
  font-size: 14px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.4;
}
</style>
